/**
 * Expands $ref + bind in orch.json layout entries and field lists (#/$defs, shared://orch).
 */

import type { FormLoaderContext, RawOrchLayoutEntry, RawOrchSchema } from './types';
import { resolveRef, applyBindings } from './uriResolver';

/** Layout/template $ref — not field-level `#/properties/...` pointers. */
function isOrchLayoutRef(entry: Record<string, unknown>): boolean {
  if (!('$ref' in entry) || typeof entry.$ref !== 'string' || 'scope' in entry) {
    return false;
  }
  const ref = entry.$ref;
  return ref.startsWith('#/$defs/') || ref.startsWith('shared://orch/');
}

function expandOrchFieldList(
  fields: unknown[],
  ctx: FormLoaderContext,
  orchDoc: Record<string, unknown>,
): unknown[] {
  const result: unknown[] = [];

  for (const item of fields) {
    if (item && typeof item === 'object') {
      const obj = item as Record<string, unknown>;
      if (isOrchLayoutRef(obj)) {
        const expanded = expandOrchEntry(obj, ctx, orchDoc);
        if (Array.isArray(expanded)) {
          result.push(...expanded);
        } else {
          result.push(expanded);
        }
        continue;
      }
    }
    result.push(item);
  }

  return result;
}

function expandNestedOrchStructure(
  node: unknown,
  ctx: FormLoaderContext,
  orchDoc: Record<string, unknown>,
  depth: number,
): unknown {
  if (node && typeof node === 'object' && !Array.isArray(node)) {
    const obj = node as Record<string, unknown>;

    if (isOrchLayoutRef(obj)) {
      return expandOrchEntry(obj, ctx, orchDoc, depth + 1);
    }

    if (Array.isArray(obj.fields)) {
      return {
        ...obj,
        fields: expandOrchFieldList(obj.fields, ctx, orchDoc),
      };
    }

    if (
      obj.fields &&
      typeof obj.fields === 'object' &&
      isOrchLayoutRef(obj.fields as Record<string, unknown>)
    ) {
      const expandedFields = expandOrchEntry(
        obj.fields as Record<string, unknown>,
        ctx,
        orchDoc,
        depth + 1,
      );
      return { ...obj, fields: expandedFields };
    }

    return obj;
  }

  return node;
}

export function expandOrchEntry(
  entry: Record<string, unknown>,
  ctx: FormLoaderContext,
  orchDoc: Record<string, unknown>,
  depth = 0,
): unknown {
  if (depth > 20) return entry;

  if (isOrchLayoutRef(entry)) {
    const resolved = resolveRef(entry.$ref, ctx, orchDoc);
    const { $ref: _discard, bind, ...overrides } = entry;
    const bindings = (bind as Record<string, string>) ?? {};

    if (Array.isArray(resolved)) {
      if (Object.keys(bindings).length > 0) {
        return applyBindings(resolved, bindings);
      }
      return resolved;
    }

    let merged = expandNestedOrchStructure(
      { ...(resolved as Record<string, unknown>), ...overrides },
      ctx,
      orchDoc,
      depth,
    ) as Record<string, unknown>;

    if (Object.keys(bindings).length > 0) {
      merged = applyBindings(merged, bindings) as Record<string, unknown>;
    }

    if ('$ref' in merged && typeof merged.$ref === 'string' && isOrchLayoutRef(merged)) {
      return expandOrchEntry(merged, ctx, orchDoc, depth + 1);
    }

    return merged;
  }

  if (Array.isArray(entry.fields)) {
    return {
      ...entry,
      fields: expandOrchFieldList(entry.fields, ctx, orchDoc),
    };
  }

  return entry;
}

export function expandOrchLayout(
  layout: RawOrchLayoutEntry[] | undefined,
  ctx: FormLoaderContext,
  orch: RawOrchSchema,
): RawOrchLayoutEntry[] {
  if (!layout) return [];

  const orchDoc = orch as unknown as Record<string, unknown>;

  return layout.map(
    (entry) => expandOrchEntry(entry as Record<string, unknown>, ctx, orchDoc) as RawOrchLayoutEntry,
  );
}

export function expandOrchSchema(orch: RawOrchSchema, ctx: FormLoaderContext): RawOrchSchema {
  const expanded: RawOrchSchema = { ...orch };

  if (orch.layout) {
    expanded.layout = expandOrchLayout(orch.layout, ctx, orch);
  }

  if (orch.steps) {
    expanded.steps = orch.steps.map((step) => ({
      ...step,
      ...(step.layout != null && {
        layout: expandOrchLayout(step.layout as RawOrchLayoutEntry[], ctx, orch),
      }),
    }));
  }

  if (orch.modes) {
    expanded.modes = Object.fromEntries(
      Object.entries(orch.modes).map(([modeName, modeCfg]) => {
        const cfg = modeCfg as Record<string, unknown>;
        if (Array.isArray(cfg.layout)) {
          return [
            modeName,
            {
              ...cfg,
              layout: expandOrchLayout(cfg.layout as RawOrchLayoutEntry[], ctx, orch),
            },
          ];
        }
        return [modeName, cfg];
      }),
    );
  }

  return expanded;
}
